import { projects as mockProjects } from '@/mocks/projects';
import { partners as mockPartners } from '@/mocks/partners';
import { products as mockProducts, type Product } from '@/mocks/products';
import { fetchAllSiteData, saveSiteData } from '@/utils/apiClient';

export type { Product };

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ExtProject {
  id: number;
  name: string;
  location: string;
  units: number;
  status: string;
  statusColor: string;
  year: string;
  area: string;
  type: string;
  description: string;
  features: string[];
  image: string;
  imageId?: string;
  youtubeUrl?: string;
  googleMapsUrl?: string;
  galleryIds?: string[];
  archived?: boolean;   // Arşivlenmiş / Gizlenmiş
  sortOrder?: number;   // Sıralama
}

export interface ExtPartner {
  id: number;
  name: string;
  sector: string;
  founded: string;
  shortDesc: string;
  fullDesc: string;
  longDesc: string;
  since: string;
  projects: number;
  website: string;
  heroImage: string;
  heroImageId?: string;
  tags: string[];
  stats: Array<{ label: string; value: string }>;
  certifications: string[];
  keyServices: Array<{ title: string; desc: string }>;
  achievements: Array<{ year: string; text: string }>;
}

export interface HeroData {
  tagline: string;
  title: string;
  subtitle: string;
  cta1Text: string;
  cta2Text: string;
  stats: Array<{ value: string; label: string; icon: string }>;
}

export interface FeatureItem {
  id: number;
  icon: string;
  title: string;
  desc: string;
  imageId?: string;
  imageUrl: string;
}

export interface CTAData {
  tag: string;
  title: string;
  subtitle: string;
  cta1Text: string;
  cta2Text: string;
  trustItems: Array<{ icon: string; text: string }>;
}

export interface TestimonialItem {
  id: number;
  quote: string;
  name: string;
  title: string;
  company: string;
  imageId?: string;
  imageUrl: string;
}

export interface AboutValue {
  id: number;
  icon: string;
  title: string;
  desc: string;
}

export interface AboutMilestone {
  id: number;
  year: string;
  event: string;
}

export interface TeamMember {
  id: number;
  name: string;
  title: string;
  department: string;
  imageId?: string;
  imageUrl: string;
}

export interface AboutData {
  heroTitle: string;
  heroSubtitle: string;
  heroTagline: string;
  heroImageId?: string;
  heroImageUrl: string;
  visionTagline: string;
  visionTitle: string;
  visionText1: string;
  visionText2: string;
  floatStatValue: string;
  floatStatLabel: string;
  valuesTagline: string;
  valuesTitle: string;
  timelineTagline: string;
  timelineTitle: string;
  teamTitle: string;
  values: AboutValue[];
  milestones: AboutMilestone[];
  team: TeamMember[];
}

export interface ChangeLogEntry {
  id: string;
  timestamp: string;
  section: string;
  sectionKey: string;
  action: string;
  details: string;
  user?: string;         // kim yaptı
  oldValue?: unknown;    // eski değer (ham)
  newValue?: unknown;    // yeni değer (ham)
  fieldPath?: string;    // hangi alan ("hero.title" gibi)
}

// Status tag group
export interface StatusTagGroup {
  id: string;            // benzersiz slug ("completed", "ongoing", ...)
  label: string;         // görünen ad
  color: string;         // "#10B981" gibi hex
  bgClass?: string;      // opsiyonel tailwind bg
  textClass?: string;    // opsiyonel tailwind text
}

// ─── Ads (server-backed, cross-platform) ───────────────────────────────────
// ─── Navigation Pages (Custom Page Builder) ──────────────────────────────────

export type CustomSectionType = 'hero' | 'text' | 'features' | 'gallery' | 'stats' | 'cta';

export interface CustomSectionHero {
  type: 'hero'; id: string;
  title: string; subtitle: string; imageUrl: string; overlayOpacity: number;
}
export interface CustomSectionText {
  type: 'text'; id: string;
  heading: string; body: string; align: 'left' | 'center' | 'right';
}
export interface CustomSectionFeature { icon: string; title: string; desc: string; }
export interface CustomSectionFeatures {
  type: 'features'; id: string;
  heading: string; items: CustomSectionFeature[];
}
export interface CustomSectionGallery {
  type: 'gallery'; id: string;
  heading: string; images: string[];
}
export interface CustomSectionStat { value: string; label: string; }
export interface CustomSectionStats {
  type: 'stats'; id: string;
  heading: string; items: CustomSectionStat[];
}
export interface CustomSectionCTA {
  type: 'cta'; id: string;
  heading: string; subtitle: string; buttonText: string; buttonLink: string;
}

export type CustomSection =
  | CustomSectionHero
  | CustomSectionText
  | CustomSectionFeatures
  | CustomSectionGallery
  | CustomSectionStats
  | CustomSectionCTA;

export interface NavPage {
  id: string;
  slug: string;        // URL path: "/hakkimizda" or "/sayfa/custom-slug"
  label: string;       // Navbar display name
  hiddenFromNav: boolean;
  builtIn: boolean;    // Built-in pages cannot be deleted
  sections?: CustomSection[];  // Only for custom pages
}

const defaultNavPages: NavPage[] = [
  { id: 'home',       slug: '/',                        label: 'Ana Sayfa',          hiddenFromNav: false, builtIn: true },
  { id: 'hakkimizda', slug: '/hakkimizda',              label: 'Hakkımızda',         hiddenFromNav: false, builtIn: true },
  { id: 'projeler',   slug: '/projelerimiz',            label: 'Ürünlerimiz',         hiddenFromNav: false, builtIn: true },
  { id: 'ortaklar',   slug: '/stratejik-ortaklarimiz',  label: 'Stratejik Ortaklar', hiddenFromNav: false, builtIn: true },
];

export interface AdItemServer {
  id: string;
  imageUrl: string;      // Sunucu URL — tüm cihazlarda çalışır
  imageFileId?: string;  // Silme için API id
  link: string;
  active: boolean;
  name: string;
  createdAt: number;
}

export interface WhatsAppSettings {
  enabled: boolean;
  phone: string;           // "905XXXXXXXXX" format
  message: string;         // default mesaj
  label: string;           // buton yazısı
  position: 'bottom-right' | 'bottom-left';
}

// Haftalık gün bazlı saat çizelgesi
export interface DaySchedule {
  enabled: boolean;   // o gün aktif mi
  start: string;      // "09:00"
  end: string;        // "18:00"
}

export interface ChatSchedule {
  autoSchedule: boolean;   // otomasyon açık mı
  timezone: string;        // "Europe/Istanbul"
  days: {
    mon: DaySchedule;
    tue: DaySchedule;
    wed: DaySchedule;
    thu: DaySchedule;
    fri: DaySchedule;
    sat: DaySchedule;
    sun: DaySchedule;
  };
}

export interface LiveChatSettings {
  enabled: boolean;
  title: string;
  subtitle: string;
  agentName: string;
  agentTitle: string;
  agentImageUrl: string;
  onlineHours: string;     // "09:00 - 18:00" gibi
  offlineMessage: string;
  formEnabled: boolean;    // form ile al veya redirect
  redirectUrl?: string;    // harici chat linki
}

// ─── Defaults ───────────────────────────────────────────────────────────────

export interface ColorPalette {
  primary: string; primaryDark: string; accent: string;
  bgBase: string; bgPanel: string; bgCard: string; border: string; textMuted: string;
}

export const defaultPalette: ColorPalette = {
  primary: "#3B82F6", primaryDark: "#3d4e6a", accent: "#7B8FAF",
  bgBase: "#050A14", bgPanel: "#0A1020", bgCard: "#141E35", border: "#2B3654", textMuted: "#7B8FAF",
};

export interface GeneralSettings {
  companyName: string;
  tagline: string;
  phone: string;
  email: string;
  address: string;
  foundedYear: string;
  socialInstagram: string;
  socialLinkedin: string;
  socialYoutube: string;
  heroTitle: string;
  heroSubtitle: string;
  ctaTitle: string;
  ctaSubtitle: string;
}

export const defaultGeneralSettings: GeneralSettings = {
  companyName: "C2TEK",
  tagline: "Uçtan Uca Bilişim Çözümleri",
  phone: "+90 312 XXX XX XX",
  email: "info@c2tek.com.tr",
  address: "Balgat Mah. 1413. Sok. Üstünkaya Apt. No:4/A Çankaya / Ankara",
  foundedYear: "2012",
  socialInstagram: "@c2tek",
  socialLinkedin: "c2tek",
  socialYoutube: "C2TEK",
  heroTitle: "Kurumsal Teknolojide Güvenilir Çözüm Ortağınız",
  heroSubtitle: "Ağ altyapısından siber güvenliğe, bulut çözümlerinden teknik servise kadar tüm bilişim ihtiyaçlarınızı tek elden yönetiyoruz",
  ctaTitle: "Dijital Dönüşüm Yolculuğunuza Bugün Başlayın",
  ctaSubtitle: "C2TEK uzman ekibiyle iletişime geçin, işletmenize özel çözümü birlikte tasarlayalım",
};

const defaultHero: HeroData = {
  tagline: "Kurumsal Bilişim Çözümleri",
  title: "Uçtan Uca Teknoloji, Tek Çözüm Ortağı",
  subtitle: "10+ yıllık tecrübemizle ağ, sistem altyapısı, siber güvenlik ve bulut çözümlerinde işletmenizin dijital dönüşümüne değer katıyoruz.",
  cta1Text: "Çözümlerimizi İncele",
  cta2Text: "Teklif Al",
  stats: [
    { value: "500+", label: "Tamamlanan Proje", icon: "ri-server-line" },
    { value: "10+", label: "Yıllık Tecrübe", icon: "ri-history-line" },
    { value: "7/24", label: "Teknik Destek", icon: "ri-customer-service-2-line" },
  ],
};

const defaultFeatures: FeatureItem[] = [
  {
    id: 1, icon: "ri-server-line", title: "Ağ & Sistem Altyapısı",
    desc: "Sunucu, depolama, kablolu/kablosuz ağ ve kesintisiz veri merkezi altyapısını kurumsal ölçekte tasarlıyor, kuruyor ve yönetiyoruz.",
    imageUrl: "https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?auto=format&fit=crop&w=600&q=80"
  },
  {
    id: 2, icon: "ri-shield-keyhole-line", title: "Siber Güvenlik",
    desc: "Uç noktadan ağ katmanına kadar çok katmanlı güvenlik mimarisi; güvenlik duvarı, EDR ve sürekli izleme ile verilerinizi koruyoruz.",
    imageUrl: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=600&q=80"
  },
  {
    id: 3, icon: "ri-cloud-line", title: "Bulut & Yönetilen Hizmetler",
    desc: "Microsoft 365, bulut göçü ve 7/24 yönetilen BT hizmetleriyle altyapınızı her zaman çalışır, güvenli ve güncel tutuyoruz.",
    imageUrl: "https://images.unsplash.com/photo-1607799279861-4dd421887fb3?auto=format&fit=crop&w=600&q=80"
  },
];

const defaultCTA: CTAData = {
  tag: "Ücretsiz Danışmanlık",
  title: "İşletmeniz İçin Doğru Teknoloji Çözümünü Birlikte Belirleyelim",
  subtitle: "BT altyapınızı değerlendirmek veya dijital dönüşüm projenizi planlamak için uzman ekibimizden ücretsiz ön görüşme talep edin.",
  cta1Text: "Ücretsiz Görüşme Talep Et",
  cta2Text: "Hemen Ara",
  trustItems: [
    { icon: "ri-verified-badge-line", text: "ISO 27001 Sertifikalı" },
    { icon: "ri-shield-check-line", text: "Çok Katmanlı Güvenlik" },
    { icon: "ri-customer-service-2-line", text: "7/24 Teknik Destek" },
    { icon: "ri-award-line", text: "10+ Yıl Tecrübe" },
  ],
};

const defaultTestimonials: TestimonialItem[] = [
  {
    id: 1,
    quote: "C2TEK ile kurduğumuz ağ ve güvenlik altyapısı sayesinde kesintisiz çalışıyor, tüm BT operasyonlarımızı tek bir çözüm ortağından yönetiyoruz.",
    name: "Mehmet Karaoğlu", title: "Bilgi İşlem Müdürü", company: "Karaoğlu Holding A.Ş.",
    imageUrl: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=400&q=80"
  },
  {
    id: 2,
    quote: "Bulut göçü ve yönetilen hizmetler projemizde C2TEK'in uzmanlığı fark yarattı; maliyetlerimizi düşürürken sistem performansımızı belirgin şekilde artırdık.",
    name: "Elif Yıldırım", title: "Operasyon Direktörü", company: "Yıldırım Lojistik",
    imageUrl: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=400&q=80"
  },
];

const defaultAbout: AboutData = {
  heroTitle: "10+ Yıllık\nTeknoloji Tecrübesi",
  heroSubtitle: "Ankara merkezli C2TEK, kurumlara uçtan uca bilişim çözümleri sunan güvenilir bir teknoloji çözüm ortağıdır.",
  heroTagline: "Kurumsal Kimliğimiz",
  heroImageUrl: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1920&q=80",
  visionTagline: "Vizyonumuz",
  visionTitle: "Kurumların En\nGüvenilir Teknoloji\nÇözüm Ortağı Olmak",
  visionText1: "2012 yılında Ankara'da kurulan C2TEK, bugün kamu ve özel sektörden yüzlerce kuruma ağ altyapısı, siber güvenlik, bulut ve teknik servis hizmetleri sunan önde gelen bilişim firmalarından biridir.",
  visionText2: "İşletmelere tek tedarikçiden uçtan uca çözüm sunmak ve dijital dönüşüm süreçlerinde sürdürülebilir değer yaratmak temel ilkemizdir.",
  floatStatValue: "500+",
  floatStatLabel: "Tamamlanan Proje",
  valuesTagline: "Değerlerimiz",
  valuesTitle: "Çalışma İlkelerimiz",
  timelineTagline: "Tarihçemiz",
  timelineTitle: "Büyüme Yolculuğumuz",
  teamTitle: "Yönetim Kadromuz",
  values: [
    { id: 1, icon: "ri-server-line", title: "Güçlü Altyapı", desc: "Sertifikalı mühendis kadrosu ve kurumsal ölçekte ağ, sunucu ve veri merkezi altyapısı." },
    { id: 2, icon: "ri-shield-keyhole-line", title: "Siber Güvenlik", desc: "ISO 27001 uyumlu süreçler, çok katmanlı güvenlik mimarisi ve sürekli tehdit izleme." },
    { id: 3, icon: "ri-customer-service-2-line", title: "7/24 Destek", desc: "Yerinde ve uzaktan kesintisiz teknik destek, SLA garantili hızlı çözüm." },
    { id: 4, icon: "ri-cloud-line", title: "Bulut Uzmanlığı", desc: "Microsoft 365, bulut göçü ve yönetilen hizmetlerde uçtan uca danışmanlık." },
    { id: 5, icon: "ri-team-line", title: "Uzman Ekip", desc: "Sertifikalı sistem, ağ ve güvenlik uzmanlarından oluşan deneyimli teknik kadro." },
  ],
  milestones: [
    { id: 1, year: "2012", event: "C2TEK, Ankara'da bilişim çözümleri firması olarak kuruldu." },
    { id: 2, year: "2015", event: "Kurumsal ağ ve sistem altyapısı projeleriyle hizmet ağı genişletildi." },
    { id: 3, year: "2018", event: "Siber güvenlik ve yönetilen BT hizmetleri portföye eklendi." },
    { id: 4, year: "2020", event: "Microsoft ve önde gelen teknoloji üreticileriyle iş ortaklıkları kuruldu." },
    { id: 5, year: "2022", event: "ISO 27001 Bilgi Güvenliği Yönetim Sistemi belgesi alındı." },
    { id: 6, year: "2024", event: "500+ tamamlanan proje ve 7/24 yönetilen hizmet altyapısına ulaşıldı." },
  ],
  team: [
    { id: 1, name: "Ahmet Cengiz", title: "Yönetim Kurulu Başkanı", department: "Üst Yönetim", imageUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=400&q=80" },
    { id: 2, name: "Selin Kaya", title: "Genel Müdür", department: "Üst Yönetim", imageUrl: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=400&q=80" },
    { id: 3, name: "Murat Demir", title: "Teknoloji Direktörü (CTO)", department: "Sistem & Ağ", imageUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80" },
    { id: 4, name: "Zeynep Arslan", title: "Siber Güvenlik Direktörü", department: "Güvenlik", imageUrl: "https://images.unsplash.com/photo-1556157382-97eda2d62296?auto=format&fit=crop&w=400&q=80" },
  ],
};

// ─── Çözümlerimiz (Solutions) ────────────────────────────────────────────────

export interface SolutionItem {
  id: number;
  icon: string;
  title: string;
  desc: string;
}

export interface SolutionsData {
  tagline: string;
  title: string;
  subtitle: string;
  items: SolutionItem[];
}

const defaultSolutions: SolutionsData = {
  tagline: "Çözümler",
  title: "Çözümlerimiz",
  subtitle: "Kurumların ihtiyaç duyduğu tüm teknoloji çözümlerini tek çatı altında, uçtan uca sunuyoruz.",
  items: [
    { id: 1, icon: "ri-apple-line", title: "Apple Ürün Grup Çözümleri", desc: "iPhone, iPad, Mac ve Apple Watch ürünleriyle kurumsal Apple ekosistemi çözümleri." },
    { id: 2, icon: "ri-smartphone-line", title: "Samsung Ürün Grup Çözümleri", desc: "Samsung mobil cihaz, tablet, monitör ve aksesuar ürünleriyle kurumsal verim çözümleri." },
    { id: 3, icon: "ri-shopping-cart-2-line", title: "HP Satış & Destek Çözümleri", desc: "HP kurumsal donanım satışı, yazılım çözümleri ve kapsamlı bölgesel destek hizmetleri." },
    { id: 4, icon: "ri-tools-line", title: "HP Yetkili Servis Çözümleri", desc: "Garantili ve garantisi bitmiş HP ürünleri için yetkili servis, bakım ve onarım hizmetleri." },
    { id: 5, icon: "ri-server-line", title: "Altyapı & Network Çözümleri", desc: "Yüksek performanslı sunucu, depolama ve kesintisiz network altyapılarıyla iş sürekliliği." },
    { id: 6, icon: "ri-store-2-line", title: "Perakende, Satış Çözümleri", desc: "POS sistemleri, mağaza içi teknoloji ve perakende sektörüne özel uçtan uca çözümler." },
    { id: 7, icon: "ri-shield-keyhole-line", title: "Siber Güvenlik Çözümleri", desc: "Endpoint'ten ağa, e-postadan kimliğe — katmanlı siber güvenlik mimarisi." },
    { id: 8, icon: "ri-vidicon-line", title: "Görüntülü İletişim ve Toplantı Çözümleri", desc: "Hibrit çalışma için konferans, video toplantı ve uzaktan iletişim altyapısı." },
  ],
};

// ─── Başarı Makaleleri (Stories) ─────────────────────────────────────────────

export interface StoryItem {
  id: number;
  imageUrl: string;
  imageId?: string;
  date: string;
  title: string;
  desc: string;
}

export interface StoriesData {
  tagline: string;
  title: string;
  subtitle: string;
  items: StoryItem[];
}

const defaultStories: StoriesData = {
  tagline: "Öğretici Makalelerimiz",
  title: "Uzmanlardan Güncel İçgörüler",
  subtitle: "Teknoloji trendleri, ipuçları ve sektör gelişmeleri hakkında güncel içerikler.",
  items: [
    { id: 1, imageUrl: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=900&q=80", date: "12 Mayıs 2024", title: "C2TEK ile HP Kiosk Çözümleri", desc: "Günümüz şirketleri, müşteri deneyimini geliştirerek rekabet avantajı elde etmek için yenilikçi teknolojilere yöneliyor. HP Kiosk Çözümleri tam da bu noktada devreye giriyor." },
    { id: 2, imageUrl: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=900&q=80", date: "5 Mayıs 2024", title: "Kurumsal Network Çözümleri Nedir?", desc: "Modern iş dünyasında şirketlerin rekabet gücünü artırmak, verimliliği yüksek tutmak ve müşteri memnuniyetini sağlamak güçlü bir ağ altyapısından geçiyor." },
    { id: 3, imageUrl: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=900&q=80", date: "28 Nisan 2024", title: "HP Operasyonel Kiralama Çözümü: Teknolojiye Erişimde Yeni Bir Yaklaşım", desc: "Modern şirketler teknolojik altyapılarını sürekli yenilemek zorundadır. Operasyonel kiralama, teknolojiye erişimde esnek ve sürdürülebilir bir alternatif sunuyor." },
    { id: 4, imageUrl: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&q=80", date: "20 Nisan 2024", title: "HPE GreenLake Çözümü: Hibrit Bulutun Geleceği", desc: "Bulut teknolojileri, şirketlerin iş süreçlerini daha esnek ve verimli hale getiriyor. HPE GreenLake ile her şeyi hizmet olarak deneyimleyin." },
  ],
};

// ─── HP Yetkili Servis Bandı ─────────────────────────────────────────────────

export interface HpFeatureItem {
  id: number;
  icon: string;
  title: string;
  sub: string;
}

export interface HpBannerData {
  title: string;
  subtitle: string;
  imageUrl: string;
  imageId?: string;
  badgeText: string;
  features: HpFeatureItem[];
}

const defaultHpBanner: HpBannerData = {
  title: "HP Yetkili Servis Merkezi",
  subtitle: "Garantili ve garantisi bitmiş tüm kurumsal ürünlere bakım ve onarım hizmeti.",
  imageUrl: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80",
  badgeText: "Authorized Service Partner",
  features: [
    { id: 1, icon: "ri-settings-3-line", title: "Orijinal Parça", sub: "%100 uyumlu" },
    { id: 2, icon: "ri-team-line", title: "Uzman Teknik Ekip", sub: "HP sertifikalı mühendisler" },
    { id: 3, icon: "ri-flashlight-line", title: "Hızlı Turnaround", sub: "Kısa sürede çözüm" },
    { id: 4, icon: "ri-shield-check-line", title: "Kurumsal Güvence", sub: "Güvenilir servis deneyimi" },
  ],
};

// ─── Ofislerimiz ─────────────────────────────────────────────────────────────

export interface OfficeBranch {
  id: number;
  imageUrl: string;
  imageId?: string;
  name: string;
  address: string;
  floorDoor?: string;
  district: string;
  phone: string;
  rating?: string;
  reviews?: number;
}

export interface OfficeCity {
  id: number;
  code: string;
  label: string;
  searchQuery: string;
  branches: OfficeBranch[];
}

export interface OfficesData {
  tagline: string;
  title: string;
  subtitle: string;
  cities: OfficeCity[];
}

const defaultOffices: OfficesData = {
  tagline: "Ofislerimiz",
  title: "Türkiye'nin Üç Stratejik Noktasında",
  subtitle: "Şehir seçin, ofisimizi haritada görün.",
  cities: [
    {
      id: 1, code: "TR34", label: "İstanbul",
      searchQuery: "Fulya+Mahallesi+B%C3%BCy%C3%BCkdere+Caddesi+Quasar+%C4%B0stanbul+Residence+No%3A76+%C5%9Ei%C5%9Fli",
      branches: [
        { id: 1, imageUrl: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=400&q=80", name: "İstanbul Merkez Ofis", address: "Fulya Mahallesi Büyükdere Caddesi Quasar İstanbul Residence No:76", floorDoor: "İç Kat: 13 Kapı: 1305", district: "Şişli/İstanbul", phone: "0 (212) 213 00 88", rating: "4,2", reviews: 28 },
        { id: 2, imageUrl: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=400&q=80", name: "İstanbul Servis Merkezi", address: "Fulya Mahallesi Büyükdere Caddesi No:76 P3 Otopark Katı", district: "Şişli/İstanbul", phone: "0 (212) 213 00 88" },
      ],
    },
    {
      id: 2, code: "TR06", label: "Ankara",
      searchQuery: "Balgat+1413.+Sok.+%C3%9Cst%C3%BCnkaya+Apt.+No%3A4%2FA+%C3%87ankaya+Ankara",
      branches: [
        { id: 1, imageUrl: "https://images.unsplash.com/photo-1431540015161-0bf868a2d407?auto=format&fit=crop&w=400&q=80", name: "Ankara Ofisimiz", address: "Balgat Mah. 1413. Sok. Üstünkaya Apt. No:4/A", district: "Çankaya/Ankara", phone: "0 (312) 219 43 83" },
      ],
    },
    {
      id: 3, code: "TR35", label: "İzmir",
      searchQuery: "Mansuro%C4%9Flu+Mah.+286%2F1+Sk.+No%3A15+K%3A6+D%3A36+Verapol+Plaza+Bayrakl%C4%B1+%C4%B0zmir",
      branches: [
        { id: 1, imageUrl: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=400&q=80", name: "İzmir Ofisimiz", address: "Mansuroğlu Mah. 286/1 Sk. No:15 K:6 D:36 Verapol Plaza", district: "Bayraklı/İzmir", phone: "0 (212) 213 00 88" },
      ],
    },
  ],
};

// ─── In-memory cache (sayfa içi hız için) ───────────────────────────────────

let _cache: Record<string, unknown> | null = null;
let _cacheLoaded = false;
const _listeners = new Set<() => void>();

function notifyListeners() {
  _listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

async function ensureCache(): Promise<Record<string, unknown>> {
  if (!_cacheLoaded) {
    try {
      _cache = await fetchAllSiteData();
    } catch {
      _cache = {};
    }
    _cacheLoaded = true;
    notifyListeners();
  }
  return _cache ?? {};
}

function getCached<T>(key: string, def: T): T {
  if (!_cache) return def;
  const val = _cache[key];
  return val !== undefined ? (val as T) : def;
}

function getCachedObject<T extends object>(key: string, def: T): T {
  const cached = getCached<T>(key, def);
  return { ...def, ...cached };
}

const sectionNames: Record<string, string> = {
  yg_site_settings: "Genel Ayarlar",
  yg_hero: "Ana Sayfa — Hero",
  yg_features: "Ana Sayfa — Neden C2TEK",
  yg_solutions: "Ana Sayfa — Çözümlerimiz",
  yg_products: "Ürünlerimiz",
  yg_cta: "Ana Sayfa — CTA",
  yg_testimonials: "Ana Sayfa — Referanslar",
  yg_stories: "Ana Sayfa — Makaleler",
  yg_hpbanner: "Ana Sayfa — HP Servis Bandı",
  yg_offices: "Ana Sayfa — Ofislerimiz",
  yg_projects: "Projeler",
  yg_partners: "Stratejik Ortaklar",
  yg_about: "Hakkımızda",
  yg_status_tags: "Proje Durum Etiketleri",
  yg_whatsapp: "WhatsApp Ayarları",
  yg_livechat: "Canlı Destek Ayarları",
  yg_nav_pages: "Navigasyon Yönetimi",
  yg_chat_schedule: "Canlı Destek Zamanlaması",
  yg_ads: "Reklam Yönetimi",
  yg_ad_settings_server: "Reklam Ayarları",
  yg_color_palette: "Renk Paleti",
  yg_feature_toggles: "Site Özellikleri",
  yg_feature_modes: "Site Özellik Modları",
};

const defaultStatusTags: StatusTagGroup[] = [
  { id: 'completed', label: 'Tamamlandı', color: '#10B981' },
  { id: 'ongoing',   label: 'Devam Ediyor', color: '#F59E0B' },
  { id: 'planning',  label: 'Planlama',     color: '#94A3B8' },
];

const defaultWhatsApp: WhatsAppSettings = {
  enabled: false,
  phone: '905551234567',
  message: 'Merhaba! Projeleriniz hakkında bilgi almak istiyorum.',
  label: 'WhatsApp ile Yazın',
  position: 'bottom-right',
};

const defaultLiveChat: LiveChatSettings = {
  enabled: false,
  title: 'Canlı Destek',
  subtitle: 'Size yardımcı olmaya hazırız',
  agentName: 'Müşteri Hizmetleri',
  agentTitle: 'Destek Ekibi',
  agentImageUrl: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=200&h=200&q=80',
  onlineHours: 'Hafta içi 09:00 - 18:00',
  offlineMessage: 'Mesajınızı bırakın, en kısa sürede döneceğiz.',
  formEnabled: true,
};

const defaultChatSchedule: ChatSchedule = {
  autoSchedule: false,
  timezone: "Europe/Istanbul",
  days: {
    mon: { enabled: true, start: "09:00", end: "18:00" },
    tue: { enabled: true, start: "09:00", end: "18:00" },
    wed: { enabled: true, start: "09:00", end: "18:00" },
    thu: { enabled: true, start: "09:00", end: "18:00" },
    fri: { enabled: true, start: "09:00", end: "18:00" },
    sat: { enabled: false, start: "10:00", end: "15:00" },
    sun: { enabled: false, start: "10:00", end: "14:00" },
  },
};

// Oturum açan kullanıcıyı almak için
function getCurrentUser(): string {
  try {
    const raw = localStorage.getItem('ygAdminSession');
    if (!raw) return 'Bilinmiyor';
    const s = JSON.parse(raw);
    return s.displayName || s.username || 'Bilinmiyor';
  } catch { return 'Bilinmiyor'; }
}

async function saveAndLog(
  key: string,
  value: unknown,
  action: string,
  details: string,
  oldValue?: unknown,
  fieldPath?: string
) {
  if (_cache) _cache[key] = value;
  await saveSiteData(key, value);
  notifyListeners();

  // Değişiklik günlüğünü güncelle
  const existing = getCached<ChangeLogEntry[]>('yg_changelog', []);
  const entry: ChangeLogEntry = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    section: sectionNames[key] || key,
    sectionKey: key,
    action,
    details,
    user: getCurrentUser(),
    oldValue,
    newValue: value,
    fieldPath,
  };
  const updated = [entry, ...existing].slice(0, 200);
  if (_cache) _cache['yg_changelog'] = updated;
  await saveSiteData('yg_changelog', updated);

  const lastChanged = getCached<Record<string, string>>('yg_last_changed', {});
  lastChanged[key] = new Date().toISOString();
  if (_cache) _cache['yg_last_changed'] = lastChanged;
  await saveSiteData('yg_last_changed', lastChanged);
}

// ─── Public Store API ────────────────────────────────────────────────────────

export const siteData = {
  // Önce cache'i yükle — uygulama başlangıcında çağrılır
  load: async () => ensureCache(),

  getGeneralSettings: (): GeneralSettings => getCachedObject('yg_site_settings', defaultGeneralSettings),
  saveGeneralSettings: async (d: GeneralSettings) => {
    const old = getCachedObject('yg_site_settings', defaultGeneralSettings);
    return saveAndLog('yg_site_settings', d, 'Güncellendi', 'Genel ayarlar düzenlendi', old);
  },

  getHero: (): HeroData => getCachedObject('yg_hero', defaultHero),
  saveHero: async (d: HeroData) => {
    const old = getCachedObject('yg_hero', defaultHero);
    return saveAndLog('yg_hero', d, 'Güncellendi', 'Hero bölümü düzenlendi', old);
  },

  getFeatures: (): FeatureItem[] => getCached('yg_features', defaultFeatures),
  saveFeatures: async (d: FeatureItem[]) => {
    const old = getCached('yg_features', defaultFeatures);
    return saveAndLog('yg_features', d, 'Güncellendi', 'Özellikler bölümü düzenlendi', old);
  },

  getCTA: (): CTAData => getCachedObject('yg_cta', defaultCTA),
  saveCTA: async (d: CTAData) => {
    const old = getCachedObject('yg_cta', defaultCTA);
    return saveAndLog('yg_cta', d, 'Güncellendi', 'CTA bölümü düzenlendi', old);
  },

  getSolutions: (): SolutionsData => getCachedObject('yg_solutions', defaultSolutions),
  saveSolutions: async (d: SolutionsData) => {
    const old = getCachedObject('yg_solutions', defaultSolutions);
    return saveAndLog('yg_solutions', d, 'Güncellendi', `${d.items.length} çözüm`, old);
  },

  getProducts: (): Product[] => getCached('yg_products', mockProducts),
  saveProducts: async (d: Product[], action = 'Güncellendi', details = '') => {
    const old = getCached('yg_products', mockProducts);
    return saveAndLog('yg_products', d, action, details || `${d.length} ürün`, old);
  },

  getStories: (): StoriesData => getCachedObject('yg_stories', defaultStories),
  saveStories: async (d: StoriesData) => {
    const old = getCachedObject('yg_stories', defaultStories);
    return saveAndLog('yg_stories', d, 'Güncellendi', `${d.items.length} makale`, old);
  },

  getHpBanner: (): HpBannerData => getCachedObject('yg_hpbanner', defaultHpBanner),
  saveHpBanner: async (d: HpBannerData) => {
    const old = getCachedObject('yg_hpbanner', defaultHpBanner);
    return saveAndLog('yg_hpbanner', d, 'Güncellendi', 'HP servis bandı düzenlendi', old);
  },

  getOffices: (): OfficesData => getCachedObject('yg_offices', defaultOffices),
  saveOffices: async (d: OfficesData) => {
    const old = getCachedObject('yg_offices', defaultOffices);
    return saveAndLog('yg_offices', d, 'Güncellendi', `${d.cities.length} şehir`, old);
  },

  getTestimonials: (): TestimonialItem[] => getCached('yg_testimonials', defaultTestimonials),
  saveTestimonials: async (d: TestimonialItem[]) => {
    const old = getCached('yg_testimonials', defaultTestimonials);
    return saveAndLog('yg_testimonials', d, 'Güncellendi', `${d.length} referans`, old);
  },

  getProjects: (): ExtProject[] => getCached('yg_projects', mockProjects as ExtProject[]),
  saveProjects: async (d: ExtProject[], action = 'Güncellendi', details = '') => {
    const old = getCached('yg_projects', mockProjects as ExtProject[]);
    return saveAndLog('yg_projects', d, action, details || `${d.length} proje`, old);
  },

  getPartners: (): ExtPartner[] => getCached('yg_partners', mockPartners as ExtPartner[]),
  savePartners: async (d: ExtPartner[], action = 'Güncellendi', details = '') => {
    const old = getCached('yg_partners', mockPartners as ExtPartner[]);
    return saveAndLog('yg_partners', d, action, details || `${d.length} ortak`, old);
  },

  getAbout: (): AboutData => getCached('yg_about', defaultAbout),
  saveAbout: async (d: AboutData, action = 'Güncellendi', details = '') => {
    const old = getCached('yg_about', defaultAbout);
    return saveAndLog('yg_about', d, action, details || 'Hakkımızda sayfası düzenlendi', old);
  },

  getChangeLog: (): ChangeLogEntry[] => getCached<ChangeLogEntry[]>('yg_changelog', []),
  clearChangeLog: async () => {
    if (_cache) _cache['yg_changelog'] = [];
    await saveSiteData('yg_changelog', []);
  },

  getLastChanged: (): Record<string, string> => getCached<Record<string, string>>('yg_last_changed', {}),

  getStatusTags: (): StatusTagGroup[] => getCached('yg_status_tags', defaultStatusTags),
  saveStatusTags: async (d: StatusTagGroup[]) => {
    const old = getCached('yg_status_tags', defaultStatusTags);
    return saveAndLog('yg_status_tags', d, 'Güncellendi', `${d.length} etiket grubu`, old);
  },

  getWhatsApp: (): WhatsAppSettings => getCached('yg_whatsapp', defaultWhatsApp),
  saveWhatsApp: async (d: WhatsAppSettings) => {
    const old = getCached('yg_whatsapp', defaultWhatsApp);
    return saveAndLog('yg_whatsapp', d, 'Güncellendi', 'WhatsApp ayarları düzenlendi', old);
  },

  getLiveChat: (): LiveChatSettings => getCached('yg_livechat', defaultLiveChat),
  saveLiveChat: async (d: LiveChatSettings) => {
    const old = getCached('yg_livechat', defaultLiveChat);
    return saveAndLog('yg_livechat', d, 'Güncellendi', 'Canlı destek ayarları düzenlendi', old);
  },

  getAds: (): AdItemServer[] => getCached('yg_ads', []),
  saveAds: async (ads: AdItemServer[]) => {
    if (_cache) _cache['yg_ads'] = ads;
    await saveSiteData('yg_ads', ads);
  },

  getAdSettings: (): { showDurationHours: number } | null => {
    const val = getCached<{ showDurationHours: number } | null>('yg_ad_settings_server', null);
    return val;
  },
  saveAdSettings: async (s: { showDurationHours: number }) => {
    if (_cache) _cache['yg_ad_settings_server'] = s;
    await saveSiteData('yg_ad_settings_server', s);
  },

  getChatSchedule: (): ChatSchedule => getCached('yg_chat_schedule', defaultChatSchedule),
  saveChatSchedule: async (d: ChatSchedule) => {
    if (_cache) _cache['yg_chat_schedule'] = d;
    await saveSiteData('yg_chat_schedule', d);
  },

  getColorPalette: (): ColorPalette => getCachedObject('yg_color_palette', defaultPalette),
  saveColorPalette: async (p: ColorPalette) => {
    const old = getCachedObject('yg_color_palette', defaultPalette);
    return saveAndLog('yg_color_palette', p, 'Güncellendi', 'Site renk paleti değiştirildi', old);
  },

  getFeatureToggles: (): Record<string, boolean> => getCached('yg_feature_toggles', {}),
  saveFeatureToggles: async (states: Record<string, boolean>) => {
    if (_cache) _cache['yg_feature_toggles'] = states;
    await saveSiteData('yg_feature_toggles', states);
    notifyListeners();
  },

  getFeatureModes: (): Record<string, "lock" | "hide"> => getCached('yg_feature_modes', {}),
  saveFeatureModes: async (modes: Record<string, "lock" | "hide">) => {
    if (_cache) _cache['yg_feature_modes'] = modes;
    await saveSiteData('yg_feature_modes', modes);
    notifyListeners();
  },

  // Anlık olarak widget'ın online mı olması gerektiğini hesaplar
  isChatOnlineNow: (): boolean => {
    const schedule = getCached<ChatSchedule>('yg_chat_schedule', defaultChatSchedule);
    if (!schedule.autoSchedule) return true; // otomasyon kapalıysa her zaman siteData.getLiveChat().enabled karar verir
    const now = new Date();
    // Türkiye saatine çevir
    const tzStr = now.toLocaleString('en-US', { timeZone: schedule.timezone || 'Europe/Istanbul' });
    const tzDate = new Date(tzStr);
    const dayIndex = tzDate.getDay(); // 0=Sun,1=Mon...
    const dayKeys: (keyof ChatSchedule['days'])[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const dayKey = dayKeys[dayIndex];
    const daySchedule = schedule.days[dayKey];
    if (!daySchedule.enabled) return false;
    const [sh, sm] = daySchedule.start.split(':').map(Number);
    const [eh, em] = daySchedule.end.split(':').map(Number);
    const curMin = tzDate.getHours() * 60 + tzDate.getMinutes();
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    return curMin >= startMin && curMin < endMin;
  },

  getNavPages: (): NavPage[] => getCached('yg_nav_pages', defaultNavPages),
  saveNavPages: async (pages: NavPage[]) => {
    if (_cache) _cache['yg_nav_pages'] = pages;
    await saveSiteData('yg_nav_pages', pages);
    notifyListeners();
  },

  subscribe: (fn: () => void) => { _listeners.add(fn); return () => _listeners.delete(fn); },
  unsubscribe: (fn: () => void) => { _listeners.delete(fn); },

  resetAll: async () => {
    const keys = ['yg_site_settings', 'yg_hero', 'yg_features', 'yg_solutions', 'yg_products', 'yg_cta', 'yg_testimonials', 'yg_stories', 'yg_hpbanner', 'yg_offices', 'yg_projects', 'yg_partners', 'yg_about', 'yg_changelog', 'yg_last_changed', 'yg_status_tags', 'yg_whatsapp', 'yg_livechat', 'yg_nav_pages', 'yg_chat_schedule', 'yg_ads', 'yg_ad_settings_server', 'yg_color_palette', 'yg_feature_toggles', 'yg_feature_modes'];
    for (const k of keys) {
      if (_cache) delete _cache[k];
      await saveSiteData(k, null);
    }
  },
};
